import React from 'react';
import { createRoot } from 'react-dom/client';
import { createMemoryRouter, RouterProvider } from 'react-router-dom';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { StudentActivityContainer } from './StudentActivity';
import { OverviewWidgetContainer } from './OverviewWidget';
import ReportsTabsWrapperContainer from './ReportsTabs';

import './index.css';

let studentActivityRoot = null;
let overviewWidgetRoot = null;
let reportsRoot = null;

const mountComponent = (containerId, Component, componentName, props) => {
  const container = document.getElementById(containerId);
  if (!container) {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to render ${componentName}: container with id "${containerId}" not found`
    );
    return null;
  }
  const root = createRoot(container);
  root.render(<Component {...props} />);
  return root;
};

// renderStudentActivity micro frontend function
window.renderStudentActivity = (containerId, externalProps, _history) => {
  studentActivityRoot = mountComponent(
    containerId,
    StudentActivityContainer,
    'StudentActivity',
    externalProps
  );
};

window.unmountStudentActivity = () => {
  if (studentActivityRoot) {
    studentActivityRoot.unmount();
  }
};

// renderOverviewWidget micro frontend function
window.renderOverviewWidget = (containerId, externalProps = {}) => {
  overviewWidgetRoot = mountComponent(
    containerId,
    OverviewWidgetContainer,
    'OverviewWidget',
    externalProps
  );
};

window.unmountOverviewWidget = () => {
  if (overviewWidgetRoot) {
    overviewWidgetRoot.unmount();
  }
};

// renderReportsTabs micro frontend function
window.renderReportsTabs = (containerId, externalProps = {}) => {
  const container = document.getElementById(containerId);
  if (!container) {
    return null;
  }

  const {
    initialPath: propInitialPath = '/',
    onTabChange,
    getLaunchUrl,
    launchActivity,
    ...rest
  } = externalProps;

  // The host may pass a stale initialPath during a tab transition (React 19
  // propagates useLocation() updates before host state flushes). When the
  // prop doesn't look like a reports path, fall back to the browser URL.
  // Only bail if neither the prop nor the actual URL is on /reports.
  let initialPath = propInitialPath;
  if (!initialPath.includes('/reports')) {
    const actualPath = window.location.pathname + window.location.search;
    if (!actualPath.includes('/reports')) {
      return null;
    }
    initialPath = actualPath;
  }

  const router = createMemoryRouter(
    [
      {
        path: '/courses/:courseId/reports',
        element: (
          <ReportsTabsWrapperContainer
            hostTabChange={onTabChange}
            getLaunchUrl={getLaunchUrl}
            launchActivity={launchActivity}
            {...rest}
          />
        ),
      },
      {
        path:
          '/courses/:courseId/reports/type/:reportType/:itemId?/:subItemId?',
        element: (
          <ReportsTabsWrapperContainer
            hostTabChange={onTabChange}
            getLaunchUrl={getLaunchUrl}
            launchActivity={launchActivity}
            {...rest}
          />
        ),
      },
    ],
    {
      initialEntries: [initialPath],
    }
  );

  // Listen for browser back/forward and sync the memory router
  const handlePopState = () => {
    // Extract the reports path from the browser URL
    const browserPath = window.location.pathname + window.location.search;
    // Only navigate if this is a reports-related path
    if (browserPath.includes('/reports')) {
      router.navigate(browserPath);
    }
  };

  // Store the handler so we can clean it up
  if (window._reportsPopStateHandler) {
    window.removeEventListener('popstate', window._reportsPopStateHandler);
  }
  window._reportsPopStateHandler = handlePopState;
  window.addEventListener('popstate', handlePopState);

  reportsRoot = createRoot(container);
  reportsRoot.render(<RouterProvider router={router} />);
};

window.unmountReportsTabs = () => {
  if (reportsRoot) {
    reportsRoot.unmount();
  }
  // Clean up the popstate listener
  if (window._reportsPopStateHandler) {
    window.removeEventListener('popstate', window._reportsPopStateHandler);
    window._reportsPopStateHandler = null;
  }
};

// Mount to root if it is not a micro frontend
// Only mount standalone app if we're on the dashboards's client domain
const isDashboardsDomain =
  window.location.hostname.includes('dashboards-client') ||
  window.location.hostname.includes('dashboards-frontend');

if (
  isDashboardsDomain &&
  !document.getElementById('StudentActivity-micro-container') &&
  !document.getElementById('ReportsTabs-micro-container') &&
  !document.getElementById('OverviewWidget-micro-container')
) {
  const container = document.getElementById('container');
  if (container) {
    const root = createRoot(container);
    root.render(
      // TODO: DECCOUPLE: check why with React.StrictMode the Provider is not working
      <>
        <App />
      </>
    );
  }
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
